import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Paginator, { PaginatorForRDT } from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { getApplicationTypeColumnLabel } from "../../config/phaseConstants";
import { useDebounce } from "../../hooks/common";
import { formatDate } from "../../utils/date";
import { useRejectedApplicationCounts, useRejectedApplications } from "../../hooks/rejectedApplications.hooks";
import { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import style from "../../components/pageHeader/pageHeader.module.css";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { TApplication } from "../../hooks/levelThreeApplication.hooks";

const RejectedApplications = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [view, setView] = useState<"table" | "card">("table");
    
    // Checkbox statuses filters
    const [showPaymentRejected, setShowPaymentRejected] = useState(true);
    const [showAppRejected, setShowAppRejected] = useState(true);
    
    const search = useDebounce(searchQuery);

    const [{ sortKey, sortOrder }, setSort] = useState<{
        sortKey: string | null;
        sortOrder: 1 | -1;
    }>({
        sortKey: null,
        sortOrder: -1,
    });

    const activeStatuses = useMemo(() => {
        const list: string[] = [];
        if (showPaymentRejected) list.push("Payment Rejected By Level 3 Reviewer");
        if (showAppRejected) list.push("Rejected By Level 3 Reviewer");
        return list;
    }, [showPaymentRejected, showAppRejected]);

    const { isLoading: isLoading1, data: counts } = useRejectedApplicationCounts({
        search,
        statuses: activeStatuses,
    });

    const { isLoading: isLoading2, data } = useRejectedApplications({
        page,
        search,
        limit: 10,
        sort: sortKey ?? "",
        sortVal: sortOrder,
        statuses: activeStatuses,
    });

    const sortedData = useMemo(() => data || [], [data]);

    const columns = useMemo(
        (): TableColumn<TApplication & { serviceName?: string }>[] => [
            {
                name: "Application ID",
                sortable: true,
                width: "190px",
                sortField: "applicationId",
                selector: (d) => d.applicationId,
                center: true,
                grow: 0.5,
                cell: (e) => (
                    <span
                        className="font-semibold text-[#E9B925] cursor-pointer underline"
                        onClick={() => {
                            navigate(`/view-application-details`, {
                                state: { id: e.id },
                            });
                        }}
                    >
                        {e.applicationId}
                    </span>
                )
            },
            {
                name: "Plot Owner's Name",
                sortable: true,
                sortField: "plot_owner_s_name",
                selector: (d) => d.plot_owner_s_name || "-",
                cell: (d) => (
                    <span className="text-center">{d.plot_owner_s_name || "-"}</span>
                ),
                center: true,
                grow: 2,
            },
            {
                name: "Application Type",
                selector: (d) => getApplicationTypeColumnLabel(d),
                center: true,
                width: "180px",
            },
            {
                name: "Status",
                width: "280px",
                center: true,
                sortable: true,
                sortField: "status",
                cell: (d) => {
                    const isPaymentRejected = d.status === "Payment Rejected By Level 3 Reviewer";
                    return (
                        <span className={`text-center font-semibold ${
                            isPaymentRejected ? "text-red-700" : "text-orange-700"
                        }`}>
                            {d.status || "-"}
                        </span>
                    );
                },
            },
            {
                name: "Name of Service",
                width: "210px",
                sortable: true,
                sortField: "name_of_service",
                selector: (d) => d.serviceName || "-",
                cell: (d) => (
                    <span className="text-center">{d.serviceName || "-"}</span>
                ),
                center: true,
            },
            {
                name: "Date of Submission",
                center: true,
                sortable: true,
                width: "240px",
                sortField: "submittedAt",
                cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
            },
            {
                name: "Action",
                center: true,
                width: "140px",
                grow: 0.5,
                cell: (e) => (
                    <div className="flex space-x-2 justify-center">
                        <ViewButton
                            onClick={() => {
                                navigate(`/view-application-details`, {
                                    state: { id: e.id },
                                });
                            }}
                        />
                    </div>
                ),
            },
        ],
        [navigate]
    );

    return (
        <div className="p-4 md:p-8">
            <div className="flex flex-col md:flex-row justify-between mb-4 gap-4 items-start md:items-center">
                <h4 className="text-2xl text-gray-600 font-bold">Rejected Applications</h4>
                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center grow md:grow-0 w-full md:w-auto">
                    
                    {/* View Switcher toggle */}
                    <button
                        className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center self-start sm:self-auto min-w-[42px]"
                        onClick={() => {
                            setView((e) => (e === "card" ? "table" : "card"));
                            setPage(1);
                        }}
                    >
                        {view === "card" ? (
                            <i className="fa fa-th-large text-primary m-0" />
                        ) : (
                            <i className="fa fa-list-ul text-primary m-0" />
                        )}
                    </button>

                    {/* Checkbox status filters */}
                    <div className="flex flex-wrap items-center gap-4 bg-white border border-[#968f8f] rounded-xl px-4 py-2 text-sm text-gray-600 font-medium">
                        <span className="font-semibold text-gray-500">Filter:</span>
                        <label className="flex items-center space-x-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={showPaymentRejected}
                                onChange={(e) => {
                                    setPage(1);
                                    setShowPaymentRejected(e.target.checked);
                                }}
                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                            />
                            <span>Payment Rejected</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={showAppRejected}
                                onChange={(e) => {
                                    setPage(1);
                                    setShowAppRejected(e.target.checked);
                                }}
                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                            />
                            <span>Application Rejected</span>
                        </label>
                    </div>

                    {/* Search box */}
                    <div className={`${style.searchContainer} w-full sm:w-auto`}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className={`${style.inputSearch} w-full`}
                            onChange={(e) => {
                                setPage(1);
                                setSearchQuery(e.target.value);
                            }}
                        />
                        <button className={`fa fa-search ${style.searchIcon}`} />
                    </div>
                </div>
            </div>

            {view === "card" ? (
                <>
                    <BoxComponent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                            {sortedData.map((row: any) => {
                                const isPaymentRejected = row.status === "Payment Rejected By Level 3 Reviewer";
                                return (
                                    <div
                                        className="p-4 rounded-2xl bg-white shadow-md flex flex-col border border-gray-200"
                                        key={row.id}
                                    >
                                        <div className="flex justify-between items-center space-x-4 mb-4">
                                            <span className={`font-semibold text-sm ${
                                                isPaymentRejected ? "text-red-700" : "text-orange-700"
                                            }`}>
                                                {row.status}
                                            </span>
                                            <div className="flex justify-center">
                                                <ViewButton
                                                    onClick={() => {
                                                        navigate(`/view-application-details`, {
                                                            state: { id: row.id },
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">Application ID</p>
                                            <p className="text-base mx-2">:</p>
                                            <p
                                                className="text-base w-2/3 text-[#E9B925] cursor-pointer underline font-semibold"
                                                onClick={() => {
                                                    navigate(`/view-application-details`, {
                                                        state: { id: row.id },
                                                    });
                                                }}
                                            >
                                                {row.applicationId}
                                            </p>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">Plot Owner's Name</p>
                                            <p className="text-base mx-2">:</p>
                                            <p className="text-base w-2/3">{row.plot_owner_s_name || "-"}</p>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">Name of Service</p>
                                            <p className="text-base mx-2">:</p>
                                            <p className="text-base w-2/3">{row.serviceName || "-"}</p>
                                        </div>
                                        <div className="mb-2 flex">
                                            <p className="font-bold text-base w-1/2">Date of Submission</p>
                                            <p className="text-base mx-2">:</p>
                                            <p className="text-base w-2/3">{row.submittedAt ? formatDate(row.submittedAt) : "-"}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Paginator
                            limit={10}
                            page={page}
                            setPage={setPage}
                            count={counts || 0}
                        />
                    </BoxComponent>
                </>
            ) : (
                <BoxComponent>
                    <DataTable
                        pagination
                        sortServer
                        paginationServer
                        className="mt-4 rounded-0"
                        columns={columns}
                        data={sortedData}
                        sortIcon={<SortIcon />}
                        customStyles={tableCustomStyles}
                        onChangePage={(p) => setPage(p)}
                        paginationTotalRows={counts || 0}
                        progressPending={isLoading1 || isLoading2}
                        onSort={(col, dir) => {
                            if (!col?.sortField) return;
                            setPage(1);
                            setSort({
                                sortKey: col.sortField || "",
                                sortOrder: dir === "asc" ? 1 : -1,
                            });
                        }}
                        paginationComponent={PaginatorForRDT}
                    />
                </BoxComponent>
            )}
        </div>
    );
};

export default RejectedApplications;
